import { useRef, useEffect, useCallback } from 'react';
import { trackEvents, getToken } from '../auth/api';

const FLUSH_INTERVAL = 5000;   // 5s batch interval
const HEARTBEAT_INTERVAL = 60000; // 60s heartbeat

function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
}

export function useTracking() {
  const queueRef = useRef([]);
  const flushTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const sessionIdRef = useRef(generateSessionId());

  const flush = useCallback(async () => {
    if (queueRef.current.length === 0) return;
    const batch = [...queueRef.current];
    queueRef.current = [];
    try {
      await trackEvents(batch);
    } catch {
      // Re-queue failed events (up to 200 max)
      queueRef.current = [...batch, ...queueRef.current].slice(0, 200);
    }
  }, []);

  const trackEvent = useCallback((eventType, section = null, eventData = null) => {
    const token = getToken();
    if (!token) return; // Not logged in

    queueRef.current.push({
      event_type: eventType,
      section: section || undefined,
      event_data: eventData || undefined,
      session_id: sessionIdRef.current,
      timestamp: new Date().toISOString(),
    });
  }, []);

  // Set up flush interval and heartbeat
  useEffect(() => {
    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL);
    heartbeatTimerRef.current = setInterval(() => {
      trackEvent('session_heartbeat');
    }, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(flushTimerRef.current);
      clearInterval(heartbeatTimerRef.current);
      // Flush remaining on unmount via fetch keepalive (no token in URL)
      if (queueRef.current.length > 0) {
        const API_BASE = process.env.REACT_APP_API_URL || '/api';
        const token = getToken();
        if (token) {
          const payload = JSON.stringify({ events: queueRef.current });
          try {
            fetch(`${API_BASE}/analytics/track.php`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: payload,
              keepalive: true,
            }).catch(() => {});
          } catch {
            // Best effort
          }
        }
      }
    };
  }, [flush, trackEvent]);

  // Also flush on page visibility change (tab switch / minimize)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [flush]);

  return { trackEvent };
}

